'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const principles = [
  {
    title: 'Relevance over volume.',
    desc: 'We don\u2019t believe more introductions automatically create better outcomes. The right conversation with the right investor is worth more than a hundred conversations with the wrong ones.',
  },
  {
    title: 'Preparation before outreach.',
    desc: 'The best investor conversations start before the first meeting. Companies that are well-prepared convert conversations into relationships more effectively.',
  },
  {
    title: 'Fit over fame.',
    desc: 'The most famous investor isn\u2019t necessarily the right investor. Stage, sector, ticket size and strategic relevance matter more than brand recognition.',
  },
  {
    title: 'Relationships over transactions.',
    desc: 'A fundraising round may last months. A strong investor relationship can last decades. We think about the long-term dynamic, not just the immediate close.',
  },
  {
    title: 'Long-term thinking.',
    desc: "The right capital partner should help with more than today's round. We consider the full journey of the company when thinking about investor fit.",
  },
];

export default function AboutPrinciples() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-t border-primary/10 bg-secondary/20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left sticky */}
          <div className="lg:sticky lg:top-32">
            <p className="section-label scroll-fade mb-5">Our Principles</p>
            <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-6">
              The values that guide{' '}
              <span className="font-serif italic text-primary">every engagement.</span>
            </h2>
            <p className="text-base text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2 mb-10">
              These principles shape how we think about every company we work with and every
              investor relationship we manage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 scroll-fade scroll-fade-delay-3">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300 text-center"
              >
                Work With Us
              </Link>
              <Link
                href="/contact"
                className="px-8 py-4 rounded-full border border-primary/30 text-foreground text-sm font-semibold tracking-widest uppercase hover:bg-primary/10 transition-all duration-300 text-center"
              >
                Join Network
              </Link>
            </div>
          </div>

          {/* Right — principles */}
          <div className="space-y-4">
            {principles.map((p, i) => (
              <div
                key={p.title}
                className={`bento-card rounded-2xl p-7 scroll-fade`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <h3 className="text-base font-semibold text-foreground mb-3 font-serif italic">
                  {p.title}
                </h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
